import { useState } from "react";
import { BlankService } from "../../../services/Integrations/BlankService.ts";
import type {
  BlankInventory,
  BlankSeries,
  LifecycleBlanks,
  ProviderId,
} from "../../../types/lifecycle.ts";
import { useLookup } from "./useLookup.ts";

export const splitBlank = (value: string): { series: string; serial: string } => {
  const raw = String(value ?? "").trim().toUpperCase().replace(/\s+/g, "");
  const match = /^([A-Z][A-Z0-9])(\d.*)$/.exec(raw);
  return match ? { series: match[1], serial: match[2] } : { series: "", serial: raw };
};

/** Group wing-backend's flat number list into the per-series shape the picker renders. */
export const toBlankSeries = (numbers: string[]): BlankSeries[] => {
  const bySeries = new Map<string, string[]>();
  for (const value of numbers) {
    if (!value) continue;
    const { series, serial } = splitBlank(value);
    if (!serial) continue;
    const list = bySeries.get(series) ?? [];
    list.push(serial);
    bySeries.set(series, list);
  }
  return [...bySeries.entries()].map(([series, available]) => ({
    series,
    label: series || "Без серия",
    providerCode: null,
    range: null,
    available,
    nextAvailable: available[0] ?? null,
  }));
};

const firstBlanks = (inventory?: BlankInventory): LifecycleBlanks => ({
  stickerSeries: inventory?.sticker[0]?.series ?? "",
  stickerNumber: inventory?.sticker[0]?.nextAvailable ?? "",
  greenCardSeries: inventory?.["green-card"][0]?.series ?? "",
  greenCardNumber: inventory?.["green-card"][0]?.nextAvailable ?? "",
});
export const paymentBlanks = (
  provider: ProviderId,
  blanks: LifecycleBlanks
): LifecycleBlanks | undefined => {
  // Only what was chosen: a cleared blank must not reach the insurer or Wing.
  const chosen = Object.fromEntries(
    Object.entries(blanks).filter(([, value]) => String(value ?? "").trim())
  ) as LifecycleBlanks;
  if (!chosen.stickerNumber) delete chosen.stickerSeries;
  if (!chosen.greenCardNumber) delete chosen.greenCardSeries;
  if (provider === "dzi" || (!chosen.stickerNumber && !chosen.greenCardNumber))
    return undefined;
  return { ...chosen, territoryId: "5" };
};
/**
 * Blanks a payment may go without: Generali takes them optionally (both or
 * neither), Lev Ins needs only the sticker. Issues and OZK payments need both.
 */
export const optionalPaymentBlanks = (
  provider: ProviderId | "",
  payment: boolean
): Record<"sticker" | "green-card", boolean> => ({
  sticker: payment && provider === "generali",
  "green-card": payment && (provider === "generali" || provider === "levins"),
});
export const validPaymentBlanks = (
  provider: ProviderId,
  blanks: LifecycleBlanks
): boolean => {
  if (provider === "levins")
    return (blanks.stickerNumber?.trim().length ?? 0) >= 6;
  // OZK issues a new sticker and a new green card with every installment payment.
  if (provider === "ozk")
    return Boolean(
      blanks.stickerNumber?.trim() &&
        blanks.greenCardSeries?.trim() &&
        blanks.greenCardNumber?.trim()
    );
  if (
    provider === "generali" &&
    (blanks.stickerNumber || blanks.greenCardNumber)
  )
    return Boolean(
      blanks.stickerNumber?.trim() &&
        blanks.greenCardSeries?.trim() &&
        blanks.greenCardNumber?.trim()
    );
  return true;
};
/**
 * @param cid      the selected local INS_COMP.CID ("Агенция на застрахователя")
 * @param agentId  the selected local AGENTS.AID. Wing holds blanks per insurer
 *                 agency and agent, so nothing is listed until both are chosen.
 */
export function useBlankInventory(
  jwt: string,
  provider: ProviderId | "",
  revision: number,
  payment = false,
  cid = "",
  agentId = ""
) {
  const [refresh, setRefresh] = useState(0);
  const key =
    jwt && provider && cid && agentId && !(payment && provider === "dzi")
      ? `${jwt}|${provider}|${cid}|${agentId}|${revision}|${refresh}`
      : "";
  const lookup = useLookup(key, async (): Promise<BlankInventory> => {
    const [stickers, greenCards] = await Promise.all([
      BlankService.fetchFreeStickers(jwt, cid, agentId),
      BlankService.fetchFreeGreenCards(jwt, cid, agentId),
    ]);
    return {
      provider: provider as ProviderId,
      sticker: toBlankSeries(stickers),
      // A green card cannot be sent without its series.
      "green-card": toBlankSeries(greenCards).filter((row) => row.series),
    };
  });
  const [chosen, setChosen] = useState<{ key: string; value: LifecycleBlanks }>(
    { key: "", value: {} }
  );
  // Issues start from the first free blanks. Payments start empty, except Lev
  // Ins, which needs only a sticker: preselecting its green card would send it.
  const first = firstBlanks(lookup.value);
  const blanks =
    chosen.key === key
      ? chosen.value
      : !payment
      ? first
      : provider === "levins"
      ? { stickerSeries: first.stickerSeries, stickerNumber: first.stickerNumber }
      : {};
  return {
    ...lookup,
    inventory: lookup.value ?? null,
    ready: Boolean(cid && agentId),
    optional: optionalPaymentBlanks(provider, payment),
    blanks,
    setBlanks: (value: LifecycleBlanks) => setChosen({ key, value }),
    refresh: () => setRefresh((value) => value + 1),
  };
}
